import { useEffect, useState } from "react";
import api, { getApiErrorMessage } from "@/lib/api";
import { fmtMoney } from "@/lib/pricing";
import { HelpTip } from "@/components/HelpTip";
import { BackButton } from "@/components/BackButton";
import { PieChart, TrendingUp, Users as UsersIcon, RefreshCw } from "lucide-react";

function monthLabel(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { month: "long", year: "numeric" });
  } catch (e) {
    return "";
  }
}

export default function Commissions() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/commissions");
      setData(data);
    } catch (e) {
      setError(getApiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  if (loading) {
    return (
      <div className="flex-1 overflow-y-auto" data-testid="commissions-page">
        <div className="px-8 py-6 max-w-5xl">
          <div className="h-6 w-40 bg-bg-surface rounded-sm animate-pulse mb-4" />
          <div className="h-32 border border-line rounded-sm animate-pulse bg-bg-surface" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex-1 overflow-y-auto" data-testid="commissions-page">
        <div className="px-8 py-6 max-w-5xl">
          <div className="border border-line rounded-sm bg-bg-surface p-6 text-sm text-destructive">
            {error}
          </div>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const role = data.role;
  const rates = data.rates;
  const period = data.period;

  return (
    <div className="flex-1 overflow-y-auto" data-testid="commissions-page">
      <div className="px-8 py-6 max-w-6xl">
        {/* Header */}
        <div className="mb-3">
          <BackButton />
        </div>
        <div className="flex items-baseline justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[11px] font-mono uppercase tracking-widest text-ink-muted">
              Compensation
            </div>
            <h1 className="text-2xl font-semibold tracking-tight text-ink mt-1">
              {role === "rep" ? "My commissions" : "Commissions"}
            </h1>
            <p className="text-sm text-ink-secondary mt-1">
              {monthLabel(period?.start)} · live from Stripe
              {data.mode && (
                <span className="ml-2 text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-sm border border-line text-ink-muted">
                  {data.mode}
                </span>
              )}
            </p>
          </div>
          <button
            onClick={load}
            data-testid="commissions-refresh-btn"
            className="flex items-center gap-2 text-xs text-ink-secondary hover:text-ink border border-line hover:bg-bg-hover px-3 py-1.5 rounded-sm transition-colors"
          >
            <RefreshCw size={12} />
            Refresh
          </button>
        </div>

        {/* Rate card */}
        <div className="mt-6 border border-line rounded-sm bg-bg-surface" data-testid="commissions-rates-card">
          <div className="px-5 py-3 border-b border-line flex items-center gap-2">
            <PieChart size={14} className="text-cyan" />
            <h2 className="text-base font-medium text-ink">Commission structure</h2>
            <HelpTip
              text="How every dollar collected splits three ways. Owners can change these in Settings → Commission rates; each column must sum to 100%."
              testId="tip-commission-structure"
            />
          </div>
          <div className="grid md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-line">
            <RatesBlock label="Setup fee (one-time)" split={rates?.setup} />
            <RatesBlock label="Monthly (MRR)" split={rates?.mrr} />
          </div>
        </div>

        {/* Role-specific bodies */}
        {role === "rep" && <RepView data={data} />}
        {role === "office_manager" && <OfficeManagerView data={data} />}
        {role === "admin" && <OwnerView data={data} />}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Small building blocks
// -----------------------------------------------------------------------------

function RatesBlock({ label, split }) {
  const rows = [
    { key: "rep", label: "Rep", pct: split?.rep, color: "#22D3C0" },
    { key: "office_manager", label: "Office Manager", pct: split?.office_manager, color: "#F4B860" },
    { key: "owner", label: "Owner", pct: split?.owner, color: "#8FA5AA" },
  ];
  return (
    <div className="p-5">
      <div className="text-[11px] font-mono uppercase tracking-widest text-ink-muted mb-3">
        {label}
      </div>
      <div className="space-y-2">
        {rows.map((r) => (
          <div key={r.key} className="flex items-center gap-3">
            <div
              className="h-1.5 rounded-sm"
              style={{ width: `${Math.max(2, r.pct || 0)}%`, backgroundColor: r.color }}
            />
            <div className="flex items-baseline justify-between flex-1 min-w-0">
              <span className="text-sm text-ink-secondary">{r.label}</span>
              <span className="text-sm font-mono text-ink tabular-nums">
                {(r.pct ?? 0).toFixed(0)}%
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Tile({ label, value, sub, testId, accent }) {
  return (
    <div
      className="border border-line rounded-sm bg-bg-surface px-5 py-4"
      data-testid={testId}
    >
      <div className="text-[11px] font-mono uppercase tracking-widest text-ink-muted">
        {label}
      </div>
      <div
        className="mt-1 text-2xl font-semibold tabular-nums"
        style={{ color: accent || "#EFF4F2" }}
      >
        {value}
      </div>
      {sub && <div className="text-[11px] text-ink-muted mt-1">{sub}</div>}
    </div>
  );
}

function TileWithTip({ label, value, sub, testId, accent, tip }) {
  return (
    <div
      className="border border-line rounded-sm bg-bg-surface px-5 py-4"
      data-testid={testId}
    >
      <div className="flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-widest text-ink-muted">
        <span>{label}</span>
        {tip && <HelpTip text={tip} size={11} />}
      </div>
      <div
        className="mt-1 text-2xl font-semibold tabular-nums"
        style={{ color: accent || "#EFF4F2" }}
      >
        {value}
      </div>
      {sub && <div className="text-[11px] text-ink-muted mt-1">{sub}</div>}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Views
// -----------------------------------------------------------------------------

function RepView({ data }) {
  const me = data.me || {};
  const totalMonth = (me.mrr_share_cents || 0) + (me.setup_share_month_cents || 0);
  return (
    <>
      <div className="mt-6 grid md:grid-cols-3 gap-4">
        <TileWithTip
          testId="rep-mrr-share-tile"
          label="Your MRR share"
          tip="Your % of MRR on every active subscription assigned to you. Recurring — this hits your commission every month the client stays."
          value={`${fmtMoney(me.mrr_share_cents)} /mo`}
          sub={`from ${fmtMoney(me.attributed_active_mrr_cents)} /mo across ${me.attributed_active_count || 0} active subs`}
          accent="#22D3C0"
        />
        <TileWithTip
          testId="rep-setup-share-tile"
          label="Setup collected this month"
          tip="Your % of every $1,499 setup fee paid this month on YOUR leads. One-time per close."
          value={fmtMoney(me.setup_share_month_cents)}
          sub={`from ${fmtMoney(me.attributed_setup_month_cents)} in setup fees paid`}
        />
        <TileWithTip
          testId="rep-total-tile"
          label="Total this month"
          tip="MRR share (monthly recurring) + setup fees collected this month. Setup is one-time; MRR compounds as your book grows."
          value={fmtMoney(totalMonth)}
          sub="MRR + setup this month"
          accent="#F4B860"
        />
      </div>
      <div className="mt-4 text-[11px] text-ink-muted leading-relaxed max-w-2xl">
        Your credit is based on the leads you own (assigned to you). When a lead
        you own signs and pays, their setup and monthly subscription roll into
        the numbers above at the rates shown up top.
      </div>
    </>
  );
}

function OfficeManagerView({ data }) {
  const pool = data.om_pool || {};
  const totals = data.totals || {};
  const rows = data.rows || [];
  return (
    <>
      <div className="mt-6 grid md:grid-cols-3 gap-4">
        <TileWithTip
          testId="om-mrr-share-tile"
          label="Your MRR share (pool)"
          tip="20% of team MRR by default. If there are multiple office managers, this is the pool they split. Recurs monthly."
          value={`${fmtMoney(pool.mrr_share_cents)} /mo`}
          sub={`from ${fmtMoney(totals.active_mrr_cents)} /mo team MRR`}
          accent="#F4B860"
        />
        <TileWithTip
          testId="om-setup-share-tile"
          label="Setup share this month"
          tip="33% of every setup fee paid this month by default. One-time per closed deal."
          value={fmtMoney(pool.setup_share_month_cents)}
          sub={`from ${fmtMoney(totals.setup_collected_month_cents)} in setup fees paid`}
        />
        <TileWithTip
          testId="om-active-tile"
          label="Active subs (team)"
          tip="Every active subscription across the team is contributing to your pool."
          value={totals.active_count || 0}
          sub={`${rows.length} rep(s) contributing`}
        />
      </div>
      <ContributorsTable
        title="Who contributed to your pool"
        rows={rows}
        showShareColumns={false}
      />
    </>
  );
}

function OwnerView({ data }) {
  const owner = data.owner_pool || {};
  const om = data.om_pool || {};
  const totals = data.totals || {};
  const unattr = data.unattributed || {};
  const rows = data.rows || [];
  return (
    <>
      <div className="mt-6 grid md:grid-cols-4 gap-4">
        <TileWithTip
          testId="owner-mrr-share-tile"
          label="Your MRR share"
          tip="Your % of MRR across every active sub. Any unattributed sub's rep-share also rolls into this bucket."
          value={`${fmtMoney(owner.mrr_share_cents)} /mo`}
          sub={`incl. unattributed pool`}
          accent="#22D3C0"
        />
        <TileWithTip
          testId="owner-setup-share-tile"
          label="Your setup share this month"
          tip="Your % of every setup fee paid this month. Includes rep-share on unattributed deals."
          value={fmtMoney(owner.setup_share_month_cents)}
          sub={`from ${fmtMoney(totals.setup_collected_month_cents)} setup total`}
        />
        <TileWithTip
          testId="owner-om-pool-tile"
          label="Office Manager pool"
          tip="The MRR pool split across all active office managers. If you have zero OMs, this is the amount you're withholding until you hire one."
          value={`${fmtMoney(om.mrr_share_cents)} /mo`}
          sub={
            (om.members || []).length
              ? (om.members || []).map((m) => m.name).join(", ")
              : "No OM assigned yet"
          }
        />
        <TileWithTip
          testId="owner-team-mrr-tile"
          label="Team MRR"
          tip="Live total MRR from Stripe across every active subscription on your account."
          value={`${fmtMoney(totals.active_mrr_cents)} /mo`}
          sub={`${totals.active_count || 0} active subs`}
          accent="#F4B860"
        />
      </div>
      <ContributorsTable
        title="Per-rep breakdown"
        rows={rows}
        showShareColumns={true}
      />
      {(unattr.mrr_cents > 0 || unattr.setup_cents_month > 0) && (
        <div className="mt-4 border border-line rounded-sm bg-bg-surface px-5 py-3 text-[13px] text-ink-secondary">
          <span className="font-mono text-[11px] uppercase tracking-widest text-ink-muted mr-2">
            Unattributed
          </span>
          {fmtMoney(unattr.mrr_cents)}/mo MRR · {fmtMoney(unattr.setup_cents_month)} setup this month.
          The rep-share of these rolls into your owner cut (no lead assignee to credit).
        </div>
      )}
    </>
  );
}

function ContributorsTable({ title, rows, showShareColumns }) {
  if (!rows || rows.length === 0) {
    return (
      <div className="mt-6 border border-line rounded-sm bg-bg-surface px-5 py-8 text-center">
        <UsersIcon size={20} className="text-cyan inline-block" />
        <div className="text-sm text-ink mt-3 font-medium">
          No paid deals attributed yet
        </div>
        <div className="text-[13px] text-ink-secondary mt-1.5 max-w-md mx-auto leading-relaxed">
          When a rep&rsquo;s lead signs the proposal and pays, their row appears here
          with everyone&rsquo;s cut applied. Attribution flows through{" "}
          <span className="font-mono text-ink">lead.assigned_rep_id</span>.
        </div>
      </div>
    );
  }
  return (
    <div className="mt-6 border border-line rounded-sm bg-bg-surface" data-testid="commissions-contributors-table">
      <div className="px-5 py-3 border-b border-line flex items-center gap-2">
        <TrendingUp size={14} className="text-cyan" />
        <h2 className="text-base font-medium text-ink">{title}</h2>
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-line text-left">
            <Th>Rep</Th>
            <Th className="text-right">Active</Th>
            <Th className="text-right">Attributed MRR</Th>
            <Th className="text-right">Setup collected (mo)</Th>
            {showShareColumns && <Th className="text-right">Rep share (mo)</Th>}
            {showShareColumns && <Th className="text-right">Rep setup share (mo)</Th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.user_id} className="border-b border-line last:border-b-0" data-testid={`commissions-row-${r.user_id}`}>
              <td className="px-4 py-3">
                <div className="text-ink">{r.name}</div>
                {r.email && (
                  <div className="text-[11px] font-mono text-ink-muted">{r.email}</div>
                )}
              </td>
              <td className="px-4 py-3 text-right font-mono tabular-nums text-ink">
                {r.attributed_active_count || 0}
              </td>
              <td className="px-4 py-3 text-right font-mono tabular-nums text-ink">
                {fmtMoney(r.attributed_active_mrr_cents)}
              </td>
              <td className="px-4 py-3 text-right font-mono tabular-nums text-ink">
                {fmtMoney(r.attributed_setup_month_cents)}
              </td>
              {showShareColumns && (
                <td className="px-4 py-3 text-right font-mono tabular-nums" style={{ color: "#22D3C0" }}>
                  {fmtMoney(r.mrr_share_cents)}
                </td>
              )}
              {showShareColumns && (
                <td className="px-4 py-3 text-right font-mono tabular-nums" style={{ color: "#22D3C0" }}>
                  {fmtMoney(r.setup_share_month_cents)}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Th({ children, className = "" }) {
  return (
    <th className={`px-4 py-2 text-[10px] font-mono uppercase tracking-widest text-ink-muted font-medium ${className}`}>
      {children}
    </th>
  );
}
